import type { TestimonialsContent } from "../../types/page-content";

interface Props {
  content: TestimonialsContent;
  sectionNumber?: string;
}

export default function TestimonialsSection({ content, sectionNumber = "07" }: Props) {
  return (
    <section className="bg-[#111111] py-28 lg:py-40 relative overflow-hidden">
      <div className="absolute right-6 top-10 hidden xl:block">
        <span className="font-heading text-white/[0.03] text-[180px] leading-none select-none">{sectionNumber}</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-14">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-0.5 bg-safety-500" />
          <span className="font-body text-safety-500 text-xs uppercase tracking-widest2 font-700">{content.eyebrow}</span>
        </div>
        <h2 className="font-heading text-white text-6xl lg:text-8xl tracking-display leading-none mb-16">
          {content.heading}
        </h2>

        {/* Featured testimonial */}
        <div className="border border-asphalt-600 bg-[#191919] p-8 lg:p-14 mb-6 relative overflow-hidden">
          <div className="absolute top-6 right-10 font-heading text-safety-500/10 text-[120px] leading-none select-none">&ldquo;</div>
          <div className="flex items-center gap-2 mb-6">
            {Array.from({ length: content.featured.stars }).map((_, i) => (
              <i key={i} className="ri-star-fill text-safety-500 text-base" />
            ))}
          </div>
          <blockquote className="font-body text-white text-xl lg:text-2xl font-500 leading-relaxed mb-8 max-w-4xl relative z-10">
            &ldquo;{content.featured.quote}&rdquo;
          </blockquote>
          <div className="flex items-center gap-4 border-t border-asphalt-600 pt-6">
            <img
              src={content.featured.avatar}
              alt={content.featured.name}
              className="w-14 h-14 object-cover object-top border-2 border-safety-500 flex-shrink-0"
            />
            <div>
              <p className="font-heading text-white text-xl tracking-display">{content.featured.name}</p>
              <p className="font-body text-asphalt-400 text-xs uppercase tracking-widest">{content.featured.project}</p>
            </div>
            <div className="ml-auto hidden md:flex items-center gap-2">
              <i className="ri-verified-badge-fill text-safety-500 text-xl" />
              <span className="font-body text-asphalt-300 text-xs uppercase tracking-widest">Verified Client</span>
            </div>
          </div>
        </div>

        {/* Testimonial grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-asphalt-600">
          {content.items.map((t) => (
            <div key={t.name} className="bg-[#191919] p-8 flex flex-col gap-5">
              <div className="flex items-center gap-1">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-safety-500 text-sm" />
                ))}
              </div>
              <p className="font-body text-asphalt-200 text-sm leading-relaxed italic flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-asphalt-700">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-10 h-10 object-cover object-top border border-safety-500/50 flex-shrink-0"
                />
                <div>
                  <p className="font-body text-white text-sm font-700">{t.name}</p>
                  <p className="font-body text-asphalt-400 text-xs">{t.project}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
